import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type { ApiActivityLog, ApiActivityFilters as ApiActivityFilterValues } from '../../../models/apiActivity';
import { fetchApiActivity } from '../../../services/admin/apiActivityService';
import { fetchAdminDashboard } from '../../../services/admin/dashboardService';
import { ApiActivityFilters } from '../components/ApiActivityFilters';
import { ApiActivityGrid, type ApiActivityRow } from '../components/ApiActivityGrid';
import type { AuditCompanyOption } from '../components/AuditFiltersPanel';

/**
 * Rows requested per load. The Syncfusion grid pages/sorts/filters this set client-side; server-side
 * filters (endpoint, user, company, status, date range) narrow it first. Matches the backend page cap.
 */
const MAX_ROWS = 200;

export function ApiActivityPage(): React.ReactElement {
  useDocumentTitle('API activity · Triplicity Backoffice');
  const { getAccessToken, acquireAccessToken } = useAuth();

  const [rows, setRows] = useState<ApiActivityLog[]>([]);
  const [companies, setCompanies] = useState<AuditCompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ApiActivityFilterValues>({});

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchApiActivity(getAccessToken, filters, 1, MAX_ROWS);
      setRows(result.items);
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Failed to load API activity.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, filters]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Company options for the filter picker (reuses the dashboard company list, same as audit logs).
  useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const companyRows = await fetchAdminDashboard(acquireAccessToken);
        if (isMounted) {
          setCompanies(companyRows.map((c) => ({ companyId: c.companyId, companyName: c.companyName })));
        }
      } catch {
        if (isMounted) {
          setCompanies([]);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [acquireAccessToken]);

  const handleFilterChange = (next: ApiActivityFilterValues): void => {
    setFilters(next);
  };

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>();
    companies.forEach((c) => map.set(c.companyId, c.companyName));
    return map;
  }, [companies]);

  const gridRows = useMemo<ApiActivityRow[]>(
    () =>
      rows.map((row) => ({
        ...row,
        companyName: (row.companyId && companyNameById.get(row.companyId)) || row.companyId || '',
        sensitiveLabel: row.sensitive ? 'Yes' : 'No',
      })),
    [rows, companyNameById]
  );

  const hasRows = rows.length > 0;

  return (
    <div>
      <PageHeader
        title="API activity"
        subtitle="Monitor authenticated API access, denied requests, and sensitive-endpoint usage across tenants."
      />

      <ApiActivityFilters filters={filters} companies={companies} onChange={handleFilterChange} />

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading API activity…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && !hasRows ? (
        <EmptyState
          title="No API activity"
          description="No API access records match the current filters."
        />
      ) : null}

      {!loading && !error && hasRows ? <ApiActivityGrid rows={gridRows} /> : null}
    </div>
  );
}
